import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, Pagination,
  Select, MenuItem, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const STATUS_CONFIG = {
  pending:            { label: "Pending",           color: "#f59e0b", bg: "#fffbeb" },
  eligible:           { label: "Eligible",           color: "#16a34a", bg: "#f0fdf4" },
  ineligible:         { label: "Not Eligible",       color: "#dc2626", bg: "#fef2f2" },
  payment_completed:  { label: "Payment Done",       color: "#336599", bg: "#f5f3ff" },
  enrolled:           { label: "Enrolled",           color: "#3b82f6", bg: "#eff6ff" },
};

const FALLBACK_STUDENTS = [
  { id: "1", firstName: "Aravind Kumar", registrationNo: "001", gradeSection: "Nilai 8 - A", overallGrade: "A", status: "pending", attendance: 96, paymentStatus: null },
  { id: "2", firstName: "Priya Sharma", registrationNo: "002", gradeSection: "Nilai 8 - A", overallGrade: "B+", status: "eligible", attendance: 92, paymentStatus: "completed" },
  { id: "3", firstName: "Karthik Rajan", registrationNo: "003", gradeSection: "Nilai 8 - B", overallGrade: "A+", status: "payment_completed", attendance: 98, paymentStatus: "completed" },
];

const ROWS_PER_PAGE = 10;

const BiliteracyEvaluation = () => {
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [markDialog, setMarkDialog] = useState({ open: false, row: null, status: "eligible", note: "" });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/seal-of-biliteracy/enrollments");
      if (res?.data) {
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        setRows(data.length > 0 ? data : FALLBACK_STUDENTS);
      } else setRows(FALLBACK_STUDENTS);
    } catch (_) { setRows(FALLBACK_STUDENTS); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterRows(rows, filterText);
  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const openMarkDialog = (row, defaultStatus = "eligible") => {
    setMarkDialog({ open: true, row, status: defaultStatus, note: "" });
  };

  const handleMark = async () => {
    const { row, status, note } = markDialog;
    try {
      await putData(`admin/seal-of-biliteracy/mark-eligibility/${row.id}`, { status, note });
      setToast({ open: true, severity: "success", message: `Student marked as ${status}!` });
      setMarkDialog({ open: false, row: null, status: "eligible", note: "" });
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Failed to update." });
    }
  };

  const handleExport = () => {
    const cols = [
      { label: "Name", field: "firstName" },
      { label: "Reg No", field: "registrationNo" },
      { label: "Grade", field: "gradeSection" },
      { label: "Overall Grade", field: "overallGrade" },
      { label: "Status", field: "status" },
    ];
    exportToCSV(cols, filtered.map(r => ({
      firstName: r.firstName,
      registrationNo: r.registrationNo,
      gradeSection: r.gradeSection,
      overallGrade: r.overallGrade,
      status: r.status,
    })), "biliteracy_evaluation.csv");
  };

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px" }} />;
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 8px" }}>
        Pages / Biliteracy Evaluation
      </p>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <WorkspacePremiumIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Biliteracy Evaluation</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Mark student eligibility for Seal of Biliteracy</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<FilterListIcon />} size="small" onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}>
            Filters
          </Button>
          <Button startIcon={<FileDownloadOutlinedIcon />} size="small" onClick={handleExport}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}>
            Export
          </Button>
        </Box>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField size="small" fullWidth placeholder="Search by name, grade, status..."
            value={filterText} onChange={e => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>
      )}

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["#", "Student Name", "Application No", "Grade", "Section", "ENROLLED", "Overall Grade", "Evaluated Date", "Attendance", "Status", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow><TableCell colSpan={11} sx={{ textAlign: "center", py: 6, color: "#8F95B2" }}>No students found</TableCell></TableRow>
              ) : pageRows.map((row, idx) => (
                <TableRow key={row.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                  <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.firstName || row.studentName}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{row.registrationNo || row.applicationNo || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>
                    {row.grade || (row.gradeSection ? row.gradeSection.split(" - ")[0] : "—")}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>
                    {row.section || (row.gradeSection ? row.gradeSection.split(" - ")[1] : "—") || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>
                    {row.enrolledDate ? new Date(row.enrolledDate).toLocaleDateString("en-GB") :
                     row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-GB") : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip label={row.overallGrade || "—"} size="small"
                      sx={{ backgroundColor: "#EAF1F8", color: "#244C77", fontWeight: 600, fontSize: "12px" }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>
                    {row.evaluatedDate ? new Date(row.evaluatedDate).toLocaleDateString("en-GB") : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151" }}>
                    {row.attendance ? `${row.attendance}%` : "—"}
                  </TableCell>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Mark Eligible">
                        <IconButton size="small" onClick={() => openMarkDialog(row, "eligible")}
                          sx={{ color: "#16a34a", "&:hover": { backgroundColor: "#f0fdf4" } }}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark Ineligible">
                        <IconButton size="small" onClick={() => openMarkDialog(row, "ineligible")}
                          sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
              sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#244C77", color: "#fff" } }} />
          </Box>
        )}
      </Paper>

      {/* Mark Dialog */}
      <Dialog open={markDialog.open} onClose={() => setMarkDialog({ ...markDialog, open: false })} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>Mark Eligibility</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#374151" }}>
              Student: <strong>{markDialog.row?.firstName}</strong>
            </Typography>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Eligibility Status</Typography>
              <Select fullWidth size="small" value={markDialog.status}
                onChange={e => setMarkDialog({ ...markDialog, status: e.target.value })}
                sx={{ borderRadius: "8px" }}>
                <MenuItem value="eligible">Eligible</MenuItem>
                <MenuItem value="ineligible">Ineligible</MenuItem>
                <MenuItem value="enrolled">Enrolled</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Evaluation Note (optional)</Typography>
              <TextField fullWidth size="small" multiline rows={2}
                placeholder="Add evaluation notes..."
                value={markDialog.note}
                onChange={e => setMarkDialog({ ...markDialog, note: e.target.value })}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setMarkDialog({ ...markDialog, open: false })} fullWidth
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151" }}>
            Cancel
          </Button>
          <Button onClick={handleMark} fullWidth variant="contained"
            sx={{ borderRadius: "10px", backgroundColor: "#2D3A5C", textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BiliteracyEvaluation;
